/**
 * AmbienteDetalhes — sub-componentes do editor de ambientes (Item C):
 * diferencia ambientes/itens MANUAIS de IMPORTADOS DO 3D (DinaBox).
 *
 *  - ComposicaoCad: leitura rica da composicao de um item CAD
 *    (dimensoes do modulo + chapas + bordas + ferragens) — read-only,
 *    vinda de `item.metadata.composicao` (ADR-0019).
 *  - DetalhesManualItem: campos editaveis de um item MANUAL
 *    (medidas L×A×P + material + acabamento + observacao).
 *  - MedidasAmbiente: medidas + observacoes do ambiente MANUAL (auto-save).
 */
import React, { useEffect, useState } from "react";
import { FiSave } from "react-icons/fi";

import { useAuth } from "../../context/AuthContext";
import { atualizarAmbiente } from "../../services/orcamentos";

// ---------- helpers ----------

export function ambienteEhCad(ambiente) {
  return (ambiente?.itens || []).some((i) => i.precificacao_origem === "CAD");
}

function fmtNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return String(n).replace(/\.0+$/, "");
}

function fmtDimsMM(dims) {
  if (!dims) return null;
  const w = fmtNum(dims.w);
  const h = fmtNum(dims.h);
  const t = fmtNum(dims.t);
  if (!w && !h && !t) return null;
  return `${w || "—"} × ${h || "—"} × ${t || "—"} mm`;
}

// estilos inline reaproveitando tokens do design system (dark/light ok)
const sBox = {
  background: "var(--surface-2, rgba(0,0,0,0.03))",
  border: "1px solid var(--border, #e5e7eb)",
  borderRadius: 8,
  padding: "10px 12px",
  marginTop: 6,
  fontSize: "0.8rem",
};
const sLabel = {
  fontSize: "0.68rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--ink-3, #9ca3af)",
  fontWeight: 600,
  marginBottom: 2,
};
const sChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  background: "var(--surface, rgba(0,0,0,0.04))",
  border: "1px solid var(--border, #e5e7eb)",
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: "0.74rem",
  margin: "2px 4px 2px 0",
};

function Grupo({ titulo, children }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={sLabel}>{titulo}</div>
      <div style={{ display: "flex", flexWrap: "wrap" }}>{children}</div>
    </div>
  );
}

/**
 * ComposicaoCad — detalhe read-only de um item importado do 3D.
 */
export function ComposicaoCad({ item }) {
  const comp = item?.metadata?.composicao || null;
  if (!comp) {
    return (
      <div style={{ ...sBox, color: "var(--ink-3, #9ca3af)" }}>
        Item importado sem detalhamento de composição (BOM). Os valores vem do
        almoxarifado.
      </div>
    );
  }
  const dims = fmtDimsMM(comp.modulo_dimensoes_mm);
  const chapas = Array.isArray(comp.chapas) ? comp.chapas : [];
  const bordas = Array.isArray(comp.bordas) ? comp.bordas : [];
  const ferragens = Array.isArray(comp.ferragens_rateadas) ? comp.ferragens_rateadas : [];
  const avisos = Array.isArray(comp.log_avisos) ? comp.log_avisos : [];

  return (
    <div style={sBox}>
      {dims && (
        <div>
          <span style={sLabel}>Dimensões (L × A × P)</span>{" "}
          <strong style={{ fontVariantNumeric: "tabular-nums" }}>{dims}</strong>
        </div>
      )}

      {chapas.length > 0 && (
        <Grupo titulo={`Chapas (${chapas.length})`}>
          {chapas.map((c, i) => (
            <span key={i} style={sChip} title={c.material_name || ""}>
              {c.material_name || `Material ${i + 1}`}
              {fmtNum(c.m2) ? ` · ${fmtNum(c.m2)} m²` : ""}
            </span>
          ))}
        </Grupo>
      )}

      {bordas.length > 0 && (
        <Grupo titulo={`Bordas (${bordas.length})`}>
          {bordas.map((b, i) => (
            <span key={i} style={sChip}>
              {b.lado || `borda ${i + 1}`}
              {fmtNum(b.metros) ? ` · ${fmtNum(b.metros)} m` : ""}
            </span>
          ))}
        </Grupo>
      )}

      {ferragens.length > 0 && (
        <Grupo titulo={`Ferragens (${ferragens.length})`}>
          {ferragens.map((f, i) => (
            <span key={i} style={sChip} title={f.nome || ""}>
              {f.nome || `ferragem ${i + 1}`}
              {fmtNum(f.qt_rateada) ? ` · ${fmtNum(f.qt_rateada)}` : ""}
            </span>
          ))}
        </Grupo>
      )}

      {avisos.length > 0 && (
        <div style={{ marginTop: 8, color: "var(--warning, #b45309)", fontSize: "0.74rem" }}>
          {avisos.map((a, i) => <div key={i}>⚠ {a}</div>)}
        </div>
      )}
    </div>
  );
}

const sField = { display: "flex", flexDirection: "column", gap: 3 };
const sInput = {
  width: "100%",
  padding: "6px 8px",
  borderRadius: 6,
  border: "1px solid var(--border, #e5e7eb)",
  background: "var(--surface, transparent)",
  color: "inherit",
  fontSize: "0.8rem",
};

/**
 * DetalhesManualItem — campos editaveis de um item manual. Usa o `valores` +
 * `setCampo` do ItemEditor pai (dirty -> botao Salvar da linha persiste).
 */
export function DetalhesManualItem({ valores, setCampo, editavel }) {
  const campo = (k, v) => setCampo(k, v);
  return (
    <div style={sBox}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        <label style={sField}>
          <span style={sLabel}>Largura (mm)</span>
          <input
            style={sInput} type="number" min="0" step="0.01"
            value={valores.largura_mm ?? ""}
            onChange={(e) => campo("largura_mm", e.target.value)}
            disabled={!editavel}
          />
        </label>
        <label style={sField}>
          <span style={sLabel}>Altura (mm)</span>
          <input
            style={sInput} type="number" min="0" step="0.01"
            value={valores.altura_mm ?? ""}
            onChange={(e) => campo("altura_mm", e.target.value)}
            disabled={!editavel}
          />
        </label>
        <label style={sField}>
          <span style={sLabel}>Profundidade (mm)</span>
          <input
            style={sInput} type="number" min="0" step="0.01"
            value={valores.profundidade_mm ?? ""}
            onChange={(e) => campo("profundidade_mm", e.target.value)}
            disabled={!editavel}
          />
        </label>
        <label style={sField}>
          <span style={sLabel}>Material / cor</span>
          <input
            style={sInput} type="text" maxLength={120}
            value={valores.material ?? ""}
            onChange={(e) => campo("material", e.target.value)}
            placeholder="Ex.: MDF Branco TX"
            disabled={!editavel}
          />
        </label>
        <label style={sField}>
          <span style={sLabel}>Acabamento</span>
          <input
            style={sInput} type="text" maxLength={120}
            value={valores.acabamento ?? ""}
            onChange={(e) => campo("acabamento", e.target.value)}
            placeholder="Ex.: Fosco, Laca"
            disabled={!editavel}
          />
        </label>
      </div>
      <label style={{ ...sField, marginTop: 8 }}>
        <span style={sLabel}>Observação</span>
        <textarea
          style={{ ...sInput, resize: "vertical", minHeight: 48 }}
          value={valores.observacao ?? ""}
          onChange={(e) => campo("observacao", e.target.value)}
          placeholder="Detalhes do item (puxadores, ferragens especiais, etc.)"
          disabled={!editavel}
        />
      </label>
      {editavel && (
        <div style={{ fontSize: "0.7rem", color: "var(--ink-3, #9ca3af)", marginTop: 4 }}>
          Clique no <FiSave size={10} style={{ verticalAlign: "middle" }} /> da linha
          para salvar os detalhes.
        </div>
      )}
    </div>
  );
}

/**
 * MedidasAmbiente — medidas + observacoes do ambiente manual. Auto-contido
 * (estado proprio + save via atualizarAmbiente). Recarrega o pai via onChange.
 */
export function MedidasAmbiente({ ambiente, editavel, onChange }) {
  const { request } = useAuth();
  const [v, setV] = useState({
    largura_mm: ambiente.largura_mm ?? "",
    altura_mm: ambiente.altura_mm ?? "",
    profundidade_mm: ambiente.profundidade_mm ?? "",
    observacoes: ambiente.observacoes ?? "",
  });
  const [dirty, setDirty] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (dirty) return;
    setV({
      largura_mm: ambiente.largura_mm ?? "",
      altura_mm: ambiente.altura_mm ?? "",
      profundidade_mm: ambiente.profundidade_mm ?? "",
      observacoes: ambiente.observacoes ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambiente.id, ambiente.largura_mm, ambiente.altura_mm, ambiente.profundidade_mm, ambiente.observacoes]);

  const setCampo = (k, val) => { setV((p) => ({ ...p, [k]: val })); setDirty(true); };

  const salvar = async () => {
    setSalvando(true);
    try {
      await atualizarAmbiente(request, ambiente.id, {
        largura_mm: v.largura_mm === "" ? null : v.largura_mm,
        altura_mm: v.altura_mm === "" ? null : v.altura_mm,
        profundidade_mm: v.profundidade_mm === "" ? null : v.profundidade_mm,
        observacoes: v.observacoes,
      });
      setDirty(false);
      await onChange();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{ ...sBox, marginTop: 4, marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={sLabel}>Medidas e observações do ambiente</span>
        {editavel && dirty && (
          <button
            type="button"
            onClick={salvar}
            disabled={salvando}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: "0.74rem", padding: "3px 10px", borderRadius: 6,
              border: "1px solid var(--accent, #2563eb)",
              background: "var(--accent, #2563eb)", color: "#fff", cursor: "pointer",
            }}
          >
            <FiSave size={11} /> Salvar
          </button>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8 }}>
        <label style={sField}>
          <span style={sLabel}>Largura (mm)</span>
          <input style={sInput} type="number" min="0" step="0.01"
            value={v.largura_mm} onChange={(e) => setCampo("largura_mm", e.target.value)} disabled={!editavel} />
        </label>
        <label style={sField}>
          <span style={sLabel}>Altura (mm)</span>
          <input style={sInput} type="number" min="0" step="0.01"
            value={v.altura_mm} onChange={(e) => setCampo("altura_mm", e.target.value)} disabled={!editavel} />
        </label>
        <label style={sField}>
          <span style={sLabel}>Profundidade (mm)</span>
          <input style={sInput} type="number" min="0" step="0.01"
            value={v.profundidade_mm} onChange={(e) => setCampo("profundidade_mm", e.target.value)} disabled={!editavel} />
        </label>
      </div>
      <label style={{ ...sField, marginTop: 8 }}>
        <span style={sLabel}>Observações do ambiente</span>
        <textarea
          style={{ ...sInput, resize: "vertical", minHeight: 44 }}
          value={v.observacoes}
          onChange={(e) => setCampo("observacoes", e.target.value)}
          placeholder="Ex.: parede com tubulação, recorte para tomada, etc."
          disabled={!editavel}
        />
      </label>
    </div>
  );
}
